import { Component, Input, OnDestroy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable } from 'rxjs';
import { PagedResult, ResourceDto } from '../../../../models/resource.models';
import { SnackbarService } from '../../../../services/snackbar.service';
import { describeApiError } from '../../../../utils/api-error.utils';

/**
 * Page-specific behavior for {@link ResourceMembershipPanelComponent}. The panel owns all the
 * shared dual-list UI and state; the data source supplies only what differs between pages
 * (which service is called, how "removable" is decided, copy strings). Build it once per page
 * (stable reference) — the panel reloads whenever a new data source is assigned.
 */
export interface ResourceMembershipDataSource {
  /** Whether the current user may add/remove members. */
  canManage: boolean;
  /** Tooltip shown on the lock icon for non-removable (primary) members. */
  lockedTooltip: string;
  /** Snackbar message shown if an add fails. */
  addErrorMessage: string;
  /** Snackbar message shown if a remove fails. */
  removeErrorMessage: string;
  /** Load the member list (server-paged, optional search filter). */
  loadAssigned(
    page: number,
    pageSize: number,
    search?: string,
  ): Observable<PagedResult<ResourceDto>>;
  /** Search candidate resources to add (server-paged); should exclude current members. */
  searchCandidates(
    page: number,
    pageSize: number,
    search: string,
  ): Observable<PagedResult<ResourceDto>>;
  /** Add a resource. The adapter performs any post-add stamping on the resource. */
  add(resource: ResourceDto): Observable<unknown>;
  /** Remove a resource. */
  remove(resource: ResourceDto): Observable<unknown>;
  /** Whether a member row can be removed (false = locked / primary). */
  isRemovable(resource: ResourceDto): boolean;
}

/**
 * Reusable "dual-list" panel for managing a paged set of resources (members) against a source:
 * an assigned list with server-side search + pagination, an add-search with its own pagination,
 * a client-side "recently removed" list, optimistic add/remove, and Primary/Secondary badges.
 * Shared by the office-details and practice-area-details admin pages via a
 * {@link ResourceMembershipDataSource} adapter.
 */
@Component({
  selector: 'app-resource-membership-panel',
  standalone: true,
  imports: [
    FormsModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './resource-membership-panel.component.html',
  styleUrls: ['./resource-membership-panel.component.scss'],
})
export class ResourceMembershipPanelComponent implements OnDestroy {
  private snackbar = inject(SnackbarService);

  /** Title shown in the panel header. */
  @Input() title = 'Resources';

  private source!: ResourceMembershipDataSource;

  /** Assigning a (new) data source loads the panel from scratch. */
  @Input({ required: true })
  set dataSource(value: ResourceMembershipDataSource) {
    this.source = value;
    if (value) {
      this.resetState();
      this.loadInitial();
    }
  }
  get dataSource(): ResourceMembershipDataSource {
    return this.source;
  }

  isExpanded = true;
  isLoading = false;

  /** Server-paged member list. */
  assigned: PagedResult<ResourceDto> | null = null;

  // Assigned-list search + pagination (server-side)
  assignedSearchQuery = '';
  assignedPageSize = 25;
  assignedPageIndex = 0;
  private assignedSearchTimeout: ReturnType<typeof setTimeout> | null = null;

  // Add-search + pagination (server-side)
  addSearchQuery = '';
  searchResults: PagedResult<ResourceDto> | null = null;
  isSearching = false;
  searchPageSize = 10;
  searchPageIndex = 0;
  private addSearchTimeout: ReturnType<typeof setTimeout> | null = null;

  // Optimistic / visual state
  newlyAddedIds = new Set<string>();
  loadingIds = new Set<string>();
  recentlyRemovedIds = new Set<string>();
  recentlyRemovedResources: ResourceDto[] = [];
  operationOrder: string[] = [];
  recentlyRemovedPageSize = 10;
  recentlyRemovedPageIndex = 0;

  get canManage(): boolean {
    return this.source?.canManage ?? false;
  }

  ngOnDestroy(): void {
    if (this.assignedSearchTimeout) {
      clearTimeout(this.assignedSearchTimeout);
    }
    if (this.addSearchTimeout) {
      clearTimeout(this.addSearchTimeout);
    }
  }

  private resetState(): void {
    this.newlyAddedIds.clear();
    this.loadingIds.clear();
    this.recentlyRemovedIds.clear();
    this.recentlyRemovedResources = [];
    this.operationOrder = [];
    this.assignedPageIndex = 0;
    this.assignedSearchQuery = '';
    this.addSearchQuery = '';
    this.searchResults = null;
    this.assigned = null;
  }

  private loadInitial(): void {
    this.isLoading = true;
    this.source
      .loadAssigned(1, this.assignedPageSize, this.assignedSearchQuery || undefined)
      .subscribe({
        next: (result) => {
          this.assigned = result;
          this.isLoading = false;
        },
        error: () => {
          this.assigned = null;
          this.isLoading = false;
        },
      });
  }

  private reloadAssigned(): void {
    this.source
      .loadAssigned(
        this.assignedPageIndex + 1,
        this.assignedPageSize,
        this.assignedSearchQuery || undefined,
      )
      .subscribe({
        next: (result) => {
          this.assigned = result;
        },
        error: () => {
          this.assigned = null;
        },
      });
  }

  onExpanded(): void {
    this.isExpanded = true;
  }

  onCollapsed(): void {
    this.isExpanded = false;
  }

  // --- Assigned list ---

  getAssignedRows(): ResourceDto[] {
    return this.sortByOperationOrder([...(this.assigned?.items ?? [])]);
  }

  getTotalAssigned(): number {
    return this.assigned?.totalCount ?? 0;
  }

  private sortByOperationOrder(resources: ResourceDto[]): ResourceDto[] {
    return resources.sort((a, b) => {
      const aSpecial =
        this.loadingIds.has(a.employeeId) || this.newlyAddedIds.has(a.employeeId);
      const bSpecial =
        this.loadingIds.has(b.employeeId) || this.newlyAddedIds.has(b.employeeId);

      if (aSpecial && !bSpecial) return -1;
      if (!aSpecial && bSpecial) return 1;

      if (aSpecial && bSpecial) {
        const ai = this.operationOrder.indexOf(a.employeeId);
        const bi = this.operationOrder.indexOf(b.employeeId);
        if (ai !== -1 && bi !== -1) return bi - ai;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
      }

      return a.lastName.localeCompare(b.lastName);
    });
  }

  onAssignedSearch(): void {
    this.assignedPageIndex = 0;
    if (this.assignedSearchTimeout) {
      clearTimeout(this.assignedSearchTimeout);
    }
    this.assignedSearchTimeout = setTimeout(() => this.reloadAssigned(), 300);
  }

  clearAssignedSearch(): void {
    if (this.assignedSearchQuery) {
      this.assignedSearchQuery = '';
      this.assignedPageIndex = 0;
      if (this.assignedSearchTimeout) {
        clearTimeout(this.assignedSearchTimeout);
      }
      this.reloadAssigned();
    }
  }

  onAssignedPageChange(event: any): void {
    this.assignedPageIndex = event.pageIndex;
    this.assignedPageSize = event.pageSize;
    this.reloadAssigned();
  }

  // --- Add search ---

  onAddSearch(event: Event): void {
    const query = (event.target as HTMLInputElement).value;
    this.searchPageIndex = 0;
    if (this.addSearchTimeout) {
      clearTimeout(this.addSearchTimeout);
    }
    if (query.trim()) {
      this.addSearchTimeout = setTimeout(() => this.performSearch(), 300);
    } else {
      this.searchResults = null;
    }
  }

  clearAddSearch(): void {
    if (this.addSearchQuery) {
      this.addSearchQuery = '';
      this.searchPageIndex = 0;
      if (this.addSearchTimeout) {
        clearTimeout(this.addSearchTimeout);
      }
      this.searchResults = null;
    }
  }

  performSearch(): void {
    if (!this.addSearchQuery.trim()) {
      this.searchResults = null;
      return;
    }

    this.isSearching = true;
    this.source
      .searchCandidates(
        this.searchPageIndex + 1,
        this.searchPageSize,
        this.addSearchQuery.trim(),
      )
      .subscribe({
        next: (result) => {
          this.searchResults = result;
          this.isSearching = false;
        },
        error: () => {
          this.searchResults = null;
          this.isSearching = false;
        },
      });
  }

  getSearchResults(): ResourceDto[] {
    // The server excludes current members; we only hide rows that are mid-flight or just added
    // (before the next refetch).
    const results = this.searchResults?.items || [];
    return results.filter(
      (r) =>
        !this.loadingIds.has(r.employeeId) && !this.newlyAddedIds.has(r.employeeId),
    );
  }

  getTotalSearchResults(): number {
    if (!this.searchResults) return 0;
    const items = this.searchResults.items || [];
    const filteredOut = items.filter(
      (r) =>
        this.loadingIds.has(r.employeeId) || this.newlyAddedIds.has(r.employeeId),
    ).length;
    return Math.max(0, this.searchResults.totalCount - filteredOut);
  }

  onSearchPageChange(event: any): void {
    this.searchPageIndex = event.pageIndex;
    this.searchPageSize = event.pageSize;
    if (this.addSearchQuery.trim()) {
      this.performSearch();
    }
  }

  // --- Recently removed list ---

  getAllRecentlyRemoved(): ResourceDto[] {
    const list = this.recentlyRemovedResources.filter((r) =>
      this.recentlyRemovedIds.has(r.employeeId),
    );
    return list.sort((a, b) => {
      const ai = this.operationOrder.indexOf(a.employeeId);
      const bi = this.operationOrder.indexOf(b.employeeId);
      if (ai !== -1 && bi !== -1) return bi - ai;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return 0;
    });
  }

  getPaginatedRecentlyRemoved(): ResourceDto[] {
    const all = this.getAllRecentlyRemoved();
    const start = this.recentlyRemovedPageIndex * this.recentlyRemovedPageSize;
    return all.slice(start, start + this.recentlyRemovedPageSize);
  }

  getTotalRecentlyRemoved(): number {
    return this.getAllRecentlyRemoved().length;
  }

  onRecentlyRemovedPageChange(event: any): void {
    this.recentlyRemovedPageIndex = event.pageIndex;
    this.recentlyRemovedPageSize = event.pageSize;
  }

  // --- Row state helpers ---

  isNewlyAdded(resource: ResourceDto): boolean {
    return this.newlyAddedIds.has(resource.employeeId);
  }

  isRowLoading(resource: ResourceDto): boolean {
    return this.loadingIds.has(resource.employeeId);
  }

  isRemovable(resource: ResourceDto): boolean {
    return this.source.isRemovable(resource);
  }

  // --- Add / remove ---

  add(resource: ResourceDto): void {
    if (!this.canManage || this.loadingIds.has(resource.employeeId)) {
      return;
    }

    this.loadingIds.add(resource.employeeId);
    this.updateOperationOrder(resource.employeeId);
    this.moveToAssignedOptimistic(resource);
    this.assignedPageIndex = 0;

    this.source.add(resource).subscribe({
      next: () => {
        this.newlyAddedIds.add(resource.employeeId);
        this.loadingIds.delete(resource.employeeId);
        this.reloadAssigned();
        if (this.addSearchQuery.trim()) {
          this.performSearch();
        }
      },
      error: (err) => {
        this.loadingIds.delete(resource.employeeId);
        this.removeFromAssignedOptimistic(resource);
        // The API's sentence first: the source's constant is only the fallback, or it would
        // always win and the server's reason would never be shown.
        this.snackbar.showError(
          describeApiError(err, this.source.addErrorMessage),
        );
      },
    });
  }

  remove(resource: ResourceDto): void {
    if (
      !this.canManage ||
      !this.isRemovable(resource) ||
      this.loadingIds.has(resource.employeeId)
    ) {
      return;
    }

    this.loadingIds.add(resource.employeeId);
    this.updateOperationOrder(resource.employeeId);
    this.moveToRecentlyRemoved(resource);
    this.assignedPageIndex = 0;

    this.source.remove(resource).subscribe({
      next: () => {
        this.loadingIds.delete(resource.employeeId);
        this.reloadAssigned();
        if (this.addSearchQuery.trim()) {
          this.performSearch();
        }
      },
      error: (err) => {
        this.loadingIds.delete(resource.employeeId);
        this.moveToAssignedOptimistic(resource);
        // The sync-managed 422 explains why the member cannot be removed by hand.
        this.snackbar.showError(
          describeApiError(err, this.source.removeErrorMessage),
        );
      },
    });
  }

  private updateOperationOrder(employeeId: string): void {
    const i = this.operationOrder.indexOf(employeeId);
    if (i !== -1) this.operationOrder.splice(i, 1);
    this.operationOrder.push(employeeId);
  }

  private moveToAssignedOptimistic(resource: ResourceDto): void {
    if (this.assigned) {
      if (!this.assigned.items.some((r) => r.employeeId === resource.employeeId)) {
        this.assigned = {
          ...this.assigned,
          items: [...this.assigned.items, resource],
          totalCount: this.assigned.totalCount + 1,
        };
      }
    }
    this.recentlyRemovedIds.delete(resource.employeeId);
    this.recentlyRemovedResources = this.recentlyRemovedResources.filter(
      (r) => r.employeeId !== resource.employeeId,
    );
  }

  private removeFromAssignedOptimistic(resource: ResourceDto): void {
    this.newlyAddedIds.delete(resource.employeeId);
    if (!this.assigned) return;
    const before = this.assigned.items.length;
    const items = this.assigned.items.filter(
      (r) => r.employeeId !== resource.employeeId,
    );
    const removed = before - items.length;
    this.assigned = {
      ...this.assigned,
      items,
      totalCount: Math.max(0, this.assigned.totalCount - removed),
    };
  }

  private moveToRecentlyRemoved(resource: ResourceDto): void {
    this.removeFromAssignedOptimistic(resource);
    this.recentlyRemovedIds.add(resource.employeeId);
    if (
      !this.recentlyRemovedResources.some(
        (r) => r.employeeId === resource.employeeId,
      )
    ) {
      this.recentlyRemovedResources = [
        ...this.recentlyRemovedResources,
        resource,
      ];
    }
  }
}
